import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputNumber, message } from 'antd';
import './Pricelist.css';
import { useGroupQuery, useGroupOperations, useItemOperations, useEnsureGroup } from '../../hooks/useOSApi';
import { GROUP_NAME, GROUP_DESCRIPTION } from '../../config/constants';

interface PriceItem {
  name: string;
  spec: string;
  price: number;
}

interface PriceListFormValues {
  videoItems: { price: number }[];
  actionItems: { price: number }[];
}

interface PriceItemProps {
  specification: string;
  price: number;
  index: number;
  sectionName: 'videoItems' | 'actionItems';
  isEditMode: boolean;
}

interface SectionTitleProps {
  title: string;
  description: string;
}

interface SectionProps {
  title: string;
  description: string;
  items: PriceItem[];
  sectionName: 'videoItems' | 'actionItems';
  isEditMode: boolean;
}

interface PricelistProps {
  showPricelistModal: boolean;
  setShowPricelistModal: (show: boolean) => void;
  isEditMode?: boolean;
  priceListData?: {
    videoItems: PriceItem[];
    actionItems: PriceItem[];
  };
  isLoading?: boolean;
}

const Pricelist: React.FC<PricelistProps> = ({
  showPricelistModal,
  setShowPricelistModal,
  isEditMode = false,
  priceListData,
  isLoading = false
}) => {
  const [form] = Form.useForm<PriceListFormValues>();
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  // 使用新的 hooks
  const { ensureGroupExists } = useEnsureGroup(GROUP_NAME.PRICE_LIST, GROUP_DESCRIPTION.PRICE_LIST);
  const { createGroupItem } = useGroupOperations();
  const { updateInformation } = useItemOperations();
  
  // 查詢價格表群組資料
  const { data: pricelistGroupData, refetch: refetchPricelistGroup } = useGroupQuery({
    groupName: GROUP_NAME.PRICE_LIST,
    queryType: 'detailsByName',
    enabled: showPricelistModal // 只在模態框開啟時查詢
  });

  // 設定初始價格資料
  const defaultVideoItems: PriceItem[] = [
    { name: '3min-video', spec: "3分鐘(含)內", price: 1000 },
    { name: '3-5min-video', spec: "3~5分鐘", price: 2000 },
    { name: '5-10min-video', spec: "5~10分鐘", price: 3000 },
    { name: '10min-video', spec: "10分鐘以上", price: 5000 }
  ];

  const defaultActionItems: PriceItem[] = [
    { name: 'sit', spec: "坐下", price: 1000 },
    { name: 'stand', spec: "站起", price: 1000 },
    { name: 'turn', spec: "轉身", price: 1000 },
    { name: 'bow', spec: "揖手", price: 1000 },
    { name: 'salute', spec: "敬禮", price: 1000 },
    { name: 'other', spec: "其他", price: 2000 }
  ];

  // 使用傳入的 priceListData 或默認值
  const [videoItems, setVideoItems] = useState<PriceItem[]>(
    priceListData?.videoItems || defaultVideoItems
  );
  const [actionItems, setActionItems] = useState<PriceItem[]>(
    priceListData?.actionItems || defaultActionItems
  );

  // 在組件加載時獲取價格表數據
  useEffect(() => {
    const loadPriceList = () => {
      if (showPricelistModal && pricelistGroupData) {
        setInitialLoading(true);
        try {
          // 從群組數據中提取價格表項目
          const pricelistItem = pricelistGroupData?.data?.[0]?.items?.[0];

          if (pricelistItem?.information) {
            const {
              videoItems: savedVideoItems,
              actionItems: savedActionItems,
            } = pricelistItem.information;

            // 更新狀態
            if (savedVideoItems && savedVideoItems.length > 0) {
              setVideoItems(savedVideoItems);
            }

            if (savedActionItems && savedActionItems.length > 0) {
              setActionItems(savedActionItems);
            }

            // 更新表單初始值
            if (form && isEditMode) {
              const initialValues: PriceListFormValues = {
                videoItems:
                  savedVideoItems?.map((item: PriceItem) => ({ price: item.price })) ||
                  defaultVideoItems.map((item: PriceItem) => ({ price: item.price })),
                actionItems:
                  savedActionItems?.map((item: PriceItem) => ({ price: item.price })) ||
                  defaultActionItems.map((item: PriceItem) => ({ price: item.price })),
              };
              form.setFieldsValue(initialValues);
            }
          }
        } catch (error) {
          console.error('載入價格表數據失敗:', error);
          message.error("無法獲取價目表數據，顯示預設價格");
        } finally {
          setInitialLoading(false);
        }
      } else if (showPricelistModal && pricelistGroupData === undefined) {
        // 數據還在載入中
        setInitialLoading(true);
      } else if (showPricelistModal) {
        // 沒有數據，使用預設值
        setInitialLoading(false);
      }
    };

    loadPriceList();
  }, [showPricelistModal, pricelistGroupData, form, isEditMode]);

  // 初始化表單值
  useEffect(() => {
    if (isEditMode && form && !initialLoading) {
      const initialValues: PriceListFormValues = {
        videoItems: videoItems.map((item) => ({ price: item.price })),
        actionItems: actionItems.map((item) => ({ price: item.price })),
      };
      form.setFieldsValue(initialValues);
    }
  }, [isEditMode, form, videoItems, actionItems, initialLoading]);

  const onFinish = async (values: PriceListFormValues) => {
    try {
      setLoading(true);
      
      // 將表單數據與原有spec數據合併
      const updatedVideoItems = videoItems.map((item, index) => ({
        name: item.name,
        spec: item.spec,
        price: values.videoItems[index].price,
      }));

      const updatedActionItems = actionItems.map((item, index) => ({
        name: item.name,
        spec: item.spec,
        price: values.actionItems[index].price,
      }));

      const priceListData = {
        videoItems: updatedVideoItems,
        actionItems: updatedActionItems,
      };

      // 確保群組存在並獲取群組 ID
      const groupId = await ensureGroupExists();

      // 檢查是否有現有的價格表項目 (修復數據解構邏輯)
      const pricelistItem = pricelistGroupData?.data?.[0]?.items?.[0];

      if (pricelistItem?.id) {
        // 更新現有項目
        await updateInformation.mutateAsync({
          item_id: pricelistItem.id,
          data: priceListData,
        });
        message.success("價目表已成功更新");
      } else {
        // 創建新的價目表項目
        await createGroupItem.mutateAsync({
          group_id: groupId,
          data: {
            name: "pricelist",
            description: "價目表設定",
            information: priceListData,
          },
        });
        message.success("價目表已成功創建");
      }

      // 重新獲取數據以更新 UI
      await refetchPricelistGroup();
      setShowPricelistModal(false);
      
    } catch (error: any) {
      console.error('保存價格表失敗:', error);
      message.error(error.message || "儲存價目表失敗，請稍後再試");
    } finally {
      setLoading(false);
    }
  };

  const PriceItem: React.FC<PriceItemProps> = ({ specification, price, index, sectionName, isEditMode }) => {
    return (
      <div className="price-item">
        <span className="price-spec">{specification}</span>
        {isEditMode ? (
          <Form.Item name={[sectionName, index, 'price']} initialValue={price} className="price-value-form-item">
            <InputNumber
              className="price-value-input"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value!.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        ) : (
          <span className="price-value">
            {price.toLocaleString('en-US')} up
          </span>
        )}
      </div>
    );
  };

  const SectionTitle: React.FC<SectionTitleProps> = ({ title, description }) => (
    <div className="section-title">
      <div className="section-title-container">
        <span className="section-title-text">{title}</span>
        <span className="section-description">{description}</span>
      </div>
    </div>
  );

  const TableHeader: React.FC = () => (
    <div className="table-header">
      <span className="header-text">規格</span>
      <span className="header-text">價格</span>
    </div>
  );

  const Divider: React.FC = () => <div className="divider"></div>;

  const Section: React.FC<SectionProps> = ({ title, description, items, sectionName, isEditMode }) => (
    <div className="price-section my-4">
      <SectionTitle title={title} description={description} />
      {isEditMode === false && <TableHeader />}
      <Divider />
      {items.map((item, index) => (
        <PriceItem
          key={index}
          specification={item.spec}
          price={item.price}
          index={index}
          sectionName={sectionName}
          isEditMode={isEditMode}
        />
      ))}
    </div>
  );

  return (
    <Modal
      open={showPricelistModal}
      onCancel={() => setShowPricelistModal(false)}
      footer={null}
      width={337}
      centered
      closable={true}
      className="price-calculation-modal"
      styles={{
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.8)"
        },
        content: {
          borderRadius: "10px",
          backgroundColor: "#FFFFFF"
        }
      }}
    >
      {initialLoading || isLoading ? (
        <div className="flex flex-col items-center justify-center p-8">
          <div className="text-center mb-4">載入價目表中...</div>
        </div>
      ) : (
        <Form form={form} onFinish={onFinish} layout="vertical">
          <div className="price-list-container">
            <div className="modal-title">
              <h2>價目表設定</h2>
            </div>

            <Section
              title="親友影音製作"
              description={`親友影音製作費用將依照影片長度計價\n目前依照朗讀語音長度初估價格`}
              items={videoItems}
              sectionName="videoItems"
              isEditMode={isEditMode}
            />

            <Section
              title="親友動作製作"
              description="親友影音製作費用將依照動作內容計價"
              items={actionItems}
              sectionName="actionItems"
              isEditMode={isEditMode}
            />
          </div>
          {isEditMode && (
            <Button type="primary" htmlType="submit" className="w-full" loading={loading}>
              儲存
            </Button>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default Pricelist; 